import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Link } from "react-router-dom";
import { ScheduleConsultationDialog } from "@/components/ScheduleConsultationDialog";
import backgroundNoise from "@/assets/background-noise.svg";
import { RelatedBlogPosts } from "@/components/RelatedBlogPosts";

const ServiceSocialMedia = () => {
  const heroReveal = useScrollReveal();
  const contentReveal = useScrollReveal();

  const offerings = [
    {
      title: "Social Media Management",
      description: "End-to-end management that builds online presence"
    },
    {
      title: "Content Creation & Design",
      description: "Visually striking, platform-specific content"
    },
    {
      title: "Social Media Strategy & Consulting",
      description: "Clear roadmaps aligned with your goals"
    },
    {
      title: "Training & Workshops",
      description: "Social Media Excellence Program for your team"
    },
    {
      title: "Analytics & Reporting",
      description: "Track engagement, conversions, and customer loyalty"
    },
    {
      title: "Community Management",
      description: "Building authentic relationships with your audience"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-4 sm:px-6 md:px-8 pt-20 sm:pt-24 md:pt-32 pb-10 sm:pb-12 md:pb-20 bg-foreground text-background overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundNoise})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          ref={heroReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            heroReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-accent text-xs sm:text-sm font-semibold mb-3 sm:mb-4 uppercase tracking-wider">
              Social Media Marketing
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight px-2">
              Connected <span className="text-accent italic">Social Growth</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-background/80 leading-relaxed max-w-3xl mx-auto px-2">
              Creating authentic connections between brands and their customers through creativity, strategy, and data-driven insights that drive meaningful results.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-background">
        <div 
          ref={contentReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            contentReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto">
            {/* Overview */}
            <div className="mb-12 sm:mb-16 md:mb-20">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">Building Communities That Convert</h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-3 sm:mb-4">
                Social media is more than posting content—it's about building authentic relationships that drive long-term business growth. We help brands create meaningful connections with their audiences through strategic content, community engagement, and data-driven optimization.
              </p>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                Our approach combines creative storytelling with platform expertise and performance analytics to turn social presence into a powerful business asset.
              </p>
              
              {/* Inline CTA */}
              <div className="bg-accent/10 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 border-2 border-accent/20">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
                  <div className="flex-1">
                    <h3 className="text-lg sm:text-xl font-bold mb-2">Ready to Amplify Your Social Impact?</h3>
                    <p className="text-muted-foreground text-sm sm:text-base">Let's create a strategy that turns followers into customers.</p>
                  </div>
                  <ScheduleConsultationDialog
                    buttonText="Get Started Today"
                    buttonClassName="px-6 py-3 bg-accent text-accent-foreground rounded-full font-semibold hover:bg-accent/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                  >
                    <button className="px-6 py-3 bg-accent text-accent-foreground rounded-full font-semibold hover:bg-accent/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
                      Get Started Today
                    </button>
                  </ScheduleConsultationDialog>
                </div>
              </div>
            </div>

            {/* Key Offerings */}
            <div className="mb-12 sm:mb-16 md:mb-20">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">What We Offer</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {offerings.map((offering, index) => (
                  <div key={index} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-muted/30 rounded-xl sm:rounded-2xl">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-accent text-xs sm:text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">{offering.title}</h3>
                      <p className="text-muted-foreground text-xs sm:text-sm">{offering.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-12 sm:mb-16 md:mb-20 p-6 sm:p-8 md:p-10 lg:p-12 bg-foreground text-background rounded-2xl sm:rounded-3xl">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">Why Choose Our Social Media Services?</h2>
              <div className="space-y-5 sm:space-y-6 mb-6 sm:mb-8">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-accent">Platform Expertise</h3>
                  <p className="text-background/80 text-sm sm:text-base">We understand the unique dynamics of each platform and tailor strategies accordingly.</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-accent">Data-Driven Decisions</h3>
                  <p className="text-background/80 text-sm sm:text-base">Every strategy is backed by analytics and continuous optimization for maximum ROI.</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-accent">Authentic Engagement</h3>
                  <p className="text-background/80 text-sm sm:text-base">We build real communities, not just follower counts, fostering loyalty and advocacy.</p>
                </div>
              </div>
              
              {/* CTA inside benefits box */}
              <div className="pt-5 sm:pt-6 border-t border-background/20">
                <p className="text-background/90 text-base sm:text-lg mb-3 sm:mb-4 font-semibold">
                  Join brands that are winning on social media
                </p>
                <ScheduleConsultationDialog
                  buttonText="Schedule Your Strategy Session"
                  buttonClassName="px-6 py-3 bg-accent text-accent-foreground rounded-full font-semibold hover:bg-accent/90 transition-all"
                >
                  <button className="px-6 py-3 bg-accent text-accent-foreground rounded-full font-semibold hover:bg-accent/90 transition-all">
                    Schedule Your Strategy Session
                  </button>
                </ScheduleConsultationDialog>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      <RelatedBlogPosts 
        category="Social Media Marketing"
        description="Explore our latest thinking on social media marketing"
      />

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-background">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight px-2">
              Ready to Transform Your <span className="text-accent italic">Social Presence?</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed px-2">
              Let's discuss how our social media services can help you build authentic connections and drive measurable growth.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2">
              <ScheduleConsultationDialog
                buttonText="Schedule a Consultation"
                buttonClassName="px-8 py-4 bg-foreground text-background rounded-full text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                <button className="px-8 py-4 bg-foreground text-background rounded-full text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105">
                  Schedule a Consultation
                </button>
              </ScheduleConsultationDialog>
              <Link to="/services">
                <button className="px-8 py-4 border-2 border-foreground/20 hover:bg-foreground/5 rounded-full text-lg font-semibold transition-all">
                  View All Services
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceSocialMedia;
